// src/components/TableUpdate.tsx
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { updateTableStatus } from '../utils/api';
import { TableUpdateProps } from '../utils/types';

const TableUpdate: React.FC<TableUpdateProps> = ({ vendorId, token }) => {
    const { tableId } = useParams<{ tableId: string }>();
    const navigate = useNavigate();
    const [status, setStatus] = useState<'locked' | 'occupied' | 'vacant'>('vacant');
    const [userId, setUserId] = useState<number | null>(null);
    const [orderId, setOrderId] = useState<number | null>(null);
    const [message, setMessage] = useState<string | null>(null);

    const isUserIdRequired = () => status !== 'vacant';

    const handleStatusChange = async () => {
        if (!tableId) {
            setMessage('Table ID is missing.');
            return;
        }

        try {
            await updateTableStatus(Number(vendorId), Number(tableId), status, status !== 'vacant' ? userId : null, status === 'occupied' ? orderId : null);

            setMessage('Table status updated successfully');

            // Navigate back after successful update
            setTimeout(() => navigate(-1), 2000); // Wait 2 seconds before navigating back
        } catch (error) {
            setMessage('Failed to update table status');
            console.error(error);
        }
    };

    return (
        <div className="max-w-lg mx-auto mt-10 p-6 bg-white shadow-md rounded-lg">
            <h2 className="text-2xl font-bold mb-4 text-center">Update Table ID {tableId} Status</h2>

            <div className="mb-4">
                <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                    Status:
                </label>
                <select
                    id="status"
                    value={status}
                    onChange={(e) => setStatus(e.target.value as 'locked' | 'occupied' | 'vacant')}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                >
                    <option value="locked">Locked</option>
                    <option value="occupied">Occupied</option>
                    <option value="vacant">Vacant</option>
                </select>
            </div>

            {status !== 'vacant' && (
                <div className="mb-4">
                    <label htmlFor="userId" className="block text-sm font-medium text-gray-700 mb-1">
                        User ID:
                    </label>
                    <input
                        id="userId"
                        type="number"
                        value={userId ?? ''}
                        onChange={(e) => setUserId(Number(e.target.value))}
                        required={isUserIdRequired()}
                        className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                </div>
            )}

            {status === 'occupied' && (
                <div className="mb-4">
                    <label htmlFor="orderId" className="block text-sm font-medium text-gray-700 mb-1">
                        Order ID:
                    </label>
                    <input
                        id="orderId"
                        type="number"
                        value={orderId ?? ''}
                        onChange={(e) => setOrderId(Number(e.target.value))}
                        required
                        className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                </div>
            )}

            <button
                onClick={handleStatusChange}
                className="w-full bg-blue-600 text-white font-semibold py-2 px-4 rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
                Update Status
            </button>

            {message && (
                <p className={`mt-4 text-center font-semibold ${message.includes('successfully') ? 'text-green-500' : 'text-red-500'}`}>
                    {message}
                </p>
            )}
        </div>
    );
};

export default TableUpdate;
